import {
  AmexRemovalNotice,
  PaymentOption,
  ReturnToAppButton,
} from "@/components";
import { useStorePaymentMethodChange } from "@/hooks";
import { useCheckoutPaid, usePaymentLoadingStore } from "@/paymentLoadingStore";
import {
  InternalPaymentOption,
  MixedPaymentOption,
  PaymentServiceOption,
  isNewCardMethod,
  isWalletMethod,
  sortForDisplay,
} from "@/queries";
import { zodResolver } from "@hookform/resolvers/zod";
import { formatCurrency } from "format-numbers";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation, Trans, keycloakToMatchiLocale } from "i18n";
import { checkoutResponse } from "matchi-api";
import { auth } from "matchi-auth";
import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Button, Checkbox, Icon, Link, RadioGroup } from "ui";
import { z } from "zod";

interface PaymentFormProps {
  onSubmit: SubmitHandler<ValidationSchema>;
  checkout: checkoutResponse;
  paymentOptions: MixedPaymentOption[];
  enableRecurring?: boolean;
}

const validationSchema = z.object({
  paymentOptionId: z.string(),
});

type ValidationSchema = z.infer<typeof validationSchema>;

export const PaymentForm = ({
  onSubmit,
  checkout,
  paymentOptions,
  enableRecurring,
}: PaymentFormProps) => {
  const { t } = useTranslation();
  const {
    verifyingRedirectResult,
    paymentFormSubmitting,
    pricingMutating,
    returnToAppUrl,
  } = usePaymentLoadingStore(state => ({
    paymentFormSubmitting: state.paymentFormSubmitting,
    verifyingRedirectResult: state.verifyingRedirectResult,
    // Set once a confirmed payment's redirect back to the app was blocked. The
    // order is paid by then, so the Pay CTA has no work left to do — it becomes
    // the way back to the app instead.
    returnToAppUrl: state.returnToAppUrl,
    // While a price-affecting mutation is in flight (promo / value card
    // apply or unapply), the Adyen checkout session may still be rebuilding
    // server-side and the Drop-in / Apple Pay component may still be primed
    // with stale session data. Keep the Pay CTA and the whole form locked
    // until the mutation and its post-success pspSession refetch settle.
    pricingMutating: state.pricingMutationsInFlight > 0,
  }));
  // Nothing the form could submit survives a paid order: the CTA below is
  // swapped out, Adyen runs with showPayButton: false so it has no button of
  // its own, and the options are locked. ApplyOffers reads the same flag.
  const paid = useCheckoutPaid();
  const formLocked =
    verifyingRedirectResult || pricingMutating || paymentFormSubmitting || paid;

  const getDefaultValues = () => ({
    paymentOptionId: checkout.isFree
      ? "FREE"
      : paymentOptions.find(({ id, disabled }) => id && !disabled)?.id,
  });

  const { handleSubmit, watch, control, reset } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    reset(getDefaultValues());
  }, [paymentOptions]);

  /**
   * TODO: We assume that if amountToPay is less than reservationAmount, it's a split booking payment for the main booker.
   * This is a naive solution and the API should tell us if it is for the main booker or not.
   */
  const isSplitMainBooker =
    parseFloat(checkout.amountToPay) < parseFloat(checkout.reservationAmount);
  const selectedPaymentOptionId = watch("paymentOptionId");
  const selectedPaymentOption = paymentOptions.find(
    ({ id }) => id === selectedPaymentOptionId,
  );

  // When enableRecurring is true and the user selects a payment method that
  // hasn't been stored before (a new card, or Google Pay / Apple Pay), we
  // require explicit consent to save it for future recurring charges before
  // the pay button unlocks.
  //
  // For a new card, consent comes from Adyen's own "Save for my next
  // payment" checkbox — its onChange (wired in paymentOptions.tsx) forwards
  // the state via the onStorePaymentMethodChange event, and that state is
  // what Adyen ultimately sends to the backend as `storePaymentMethod`.
  //
  // Adyen wallets (Google Pay / Apple Pay) don't provide a save checkbox,
  // so we render our own below. Unlike the card checkbox, this one is a
  // pure UI/legal convention — its value is NOT sent to the backend. The
  // backend stores the wallet automatically for recurring purchases; we
  // only show the checkbox to obtain explicit consent from the shopper.
  //
  // Stored cards (storedPaymentMethodId present) are already saved, so no
  // extra consent is needed — the button stays enabled.
  const selectedMethod =
    selectedPaymentOption?.type === "PAYMENT_SERVICE"
      ? (selectedPaymentOption as PaymentServiceOption).method
      : undefined;
  const requiresRecurringConsent =
    !!enableRecurring &&
    (isNewCardMethod(selectedMethod) || isWalletMethod(selectedMethod));
  const showWalletConsentCheckbox =
    !!enableRecurring && isWalletMethod(selectedMethod);

  const [storeConsent, setStoreConsent] = useState(false);

  useStorePaymentMethodChange(setStoreConsent);

  useEffect(() => {
    if (!requiresRecurringConsent) setStoreConsent(false);
  }, [requiresRecurringConsent]);

  // Wallets and Swish lead the rendered list; `paymentOptions` itself stays in
  // preference order so getDefaultValues() above is unaffected.
  const displayedOptions = useMemo(
    () => sortForDisplay(paymentOptions),
    [paymentOptions],
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        verifyingRedirectResult || pricingMutating ? "pointer-events-none" : ""
      }
    >
      <AmexRemovalNotice checkout={checkout} paymentOptions={paymentOptions} />

      {!checkout.isFree && (
        <div className="mb-2">
          <Controller
            control={control}
            name="paymentOptionId"
            render={({ field, fieldState }) => (
              <>
                <RadioGroup.Root
                  asChild
                  disabled={formLocked}
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <div className="border-default mb-4 rounded-xl border">
                    {displayedOptions.map(option => (
                      <PaymentOption
                        key={option.id}
                        option={option}
                        selected={option.id === selectedPaymentOption?.id}
                      />
                    ))}
                  </div>
                </RadioGroup.Root>
                {fieldState.error && fieldState.error.message}
              </>
            )}
          />
        </div>
      )}

      {selectedPaymentOption?.type === "PAYMENT_SERVICE" &&
        (selectedPaymentOption as PaymentServiceOption).method?.type ===
          "giftcard" && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="py-3 px-4 border border-default rounded-xl bg-gray-200 mb-4">
                <div className="flex items-center mb-1 space-x-2">
                  <Icon icon="CircleInfoSolid" className="w-4 text-brand" />
                  <span className="font-medium text-black">
                    {t("Can't find your card number?")}
                  </span>
                </div>
                <span className="text-gray-1200">
                  <Trans
                    i18nKey="giftcard.info"
                    components={{
                      1: (
                        <a
                          className="text-green-700 font-medium underline"
                          href="https://giftcards.matchi.com/Home/FAQ"
                        />
                      ),
                    }}
                  />
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

      {isSplitMainBooker && !checkout.usePreAuth && (
        <div className="mb-2 rounded-xl bg-gray-300 p-4">
          <h4 className="mb-2 flex items-center space-x-2 font-medium">
            <Icon icon="CircleInfoSolid" className="text-signal-warning" />
            <span className="leading-none">
              {t("Important notice")}
            </span>
          </h4>
          <p>
            <Trans
              i18nKey="splitBooking.chargeNotice"
              values={{
                amount: formatCurrency({
                  locale: keycloakToMatchiLocale(auth.getLocale()),
                  number: parseFloat(checkout.reservationAmount),
                  currency: checkout.price?.currency!,
                }),
              }}
              components={{
                1: <span className="font-medium" />,
              }}
            />
          </p>
        </div>
      )}

      {/*Milestone 1 text: Does not contain amount adjustments to orders*/}
      {checkout.usePreAuth && !checkout.useAmountAdjustment && (
        <div className="mb-2 rounded-xl bg-gray-300 p-4">
          <h4 className="mb-2 flex items-center space-x-2 font-medium">
            <Icon icon="CircleInfoSolid" className="text-signal-warning" />
            <span className="leading-none">
              {t("Important notice")}
            </span>
          </h4>
          <p>
            <Trans
              i18nKey="splitBooking.reservationNotice"
              values={{
                amount: formatCurrency({
                  locale: keycloakToMatchiLocale(auth.getLocale()),
                  number: parseFloat(checkout.reservationAmount),
                  currency: checkout.price?.currency!,
                }),
              }}
              components={{
                1: <span className="font-medium" />,
              }}
            />
          </p>
        </div>
      )}

      {/* Milestone 2 text: Contains amount adjustments to orders*/}
      {checkout.usePreAuth && checkout.useAmountAdjustment && (
        <div className="mb-2 rounded-xl bg-gray-300 p-4">
          <h4 className="mb-2 flex items-center space-x-2 font-medium">
            <Icon icon="CircleInfoSolid" className="text-signal-warning" />
            <span className="leading-none">
              {t("Important notice")}
            </span>
          </h4>
          <p>
            <Trans
              i18nKey="splitBooking.reservationNotice"
              values={{
                amount: formatCurrency({
                  locale: keycloakToMatchiLocale(auth.getLocale()),
                  number: parseFloat(checkout.reservationAmount),
                  currency: checkout.price?.currency!,
                }),
              }}
              components={{
                1: <span className="font-medium" />,
              }}
            />
          </p>
        </div>
      )}

      {requiresRecurringConsent && (
        <div className="mb-4 rounded-lg border border-amber-400 bg-amber-50 p-3">
          <p className="text-sm text-gray-1200">
            {showWalletConsentCheckbox
              ? t(
                  `This transaction requires us to save your card details. To continue, tick the "Save for my next purchase" box below. You can manage your saved cards and choose which one to use as default for future charges anytime in your MATCHi account settings.`,
                )
              : t(
                  `This transaction requires us to save your card details. To continue, tick the "Save for my next payment" box next to your card details above. You can manage your saved cards and choose which one to use as default for future charges anytime in your MATCHi account settings.`,
                )}
          </p>
        </div>
      )}

      {showWalletConsentCheckbox && (
        <div className="mb-3">
          <Checkbox
            name="walletRecurringConsent"
            checked={storeConsent}
            onCheckedChange={value => setStoreConsent(value === true)}
            label={t("Save for my next purchase")}
          />
        </div>
      )}

      {returnToAppUrl ? (
        <ReturnToAppButton url={returnToAppUrl} />
      ) : (
        <div className="space-y-2">
          <Button
            loading={paymentFormSubmitting}
            disabled={formLocked || (requiresRecurringConsent && !storeConsent)}
            block
            size="lg"
          >
            <PayButtonLabel
              checkout={checkout}
              paymentOption={selectedPaymentOption}
              showReservationText={isSplitMainBooker}
            />
          </Button>
          <p className="text-gray-1100 text-center leading-normal">
            <Trans
              i18nKey="terms.acceptance"
              values={{
                buttonLabel: checkout.isFree
                  ? t("Confirm order")
                  : isSplitMainBooker
                    ? t("Reserve")
                    : t("Pay"),
                facilityInfo: checkout.facilityOrgNr
                  ? `${
                      Boolean(checkout.facilityLegalEntityName)
                        ? checkout.facilityLegalEntityName
                        : checkout.facilityName
                    }, ${checkout.facilityOrgNr}`
                  : Boolean(checkout.facilityLegalEntityName)
                    ? checkout.facilityLegalEntityName
                    : checkout.facilityName,
              }}
              components={{
                1: checkout.facilityPrivacyPolicyUrl ? (
                  <Link
                    variant="secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={checkout.facilityPrivacyPolicyUrl}
                  />
                ) : (
                  <span className="inline font-medium text-black" />
                ),
                2: (
                  <Link
                    variant="secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.matchi.se/home/useragreement"
                  />
                ),
              }}
            />
          </p>
        </div>
      )}
    </form>
  );
};

const PayButtonLabel = ({
  checkout,
  paymentOption,
  showReservationText,
}: {
  checkout: checkoutResponse;
  paymentOption?: MixedPaymentOption;
  showReservationText: boolean;
}) => {
  const { t } = useTranslation();

  // Free orders
  if (checkout.isFree) return <>{t("Confirm order")}</>;

  // Adyen gift cards
  if (
    paymentOption?.type === "PAYMENT_SERVICE" &&
    (paymentOption as PaymentServiceOption).method?.type === "giftcard"
  ) {
    return <>{t("Redeem gift card")}</>;
  }

  // Punch cards
  if (paymentOption?.type === "COUPON") {
    const option = paymentOption as InternalPaymentOption;
    const punchesNeeded = option.method?.punchesNeeded
      ? parseInt(option.method?.punchesNeeded)
      : 1;

    if (punchesNeeded <= 1) return <>{t("Pay with 1 punch")}</>;

    return <>{t("Pay with {punchesNeeded} punches", { punchesNeeded })}</>;
  }

  // Adyen payment methods
  return showReservationText ? (
    <>
      {t("Reserve {amount}", {
        amount: formatCurrency({
          locale: keycloakToMatchiLocale(auth.getLocale()),
          number: parseFloat(checkout.reservationAmount),
          currency: checkout.price?.currency!,
        }),
      })}
    </>
  ) : (
    <>
      {t("Pay {amount}", {
        amount: formatCurrency({
          locale: keycloakToMatchiLocale(auth.getLocale()),
          number: parseFloat(checkout.amountToPay),
          currency: checkout.price?.currency!,
        }),
      })}
    </>
  );
};
